/**
 * THE PUBLIC PAGE'S FIGURES, FOR THE PREVIEW (design 24, batch 2, B2-PREVIEW; the owner, 2026-10-12).
 * The product's public author page draws a row of three under its specialization line - every read
 * of the author's articles, how many articles, how many followers (`moonx-web-product/
 * author-mr-moon.html`, its "stat-row"). The preview draws only what the public page can show
 * (T28's rule), and the reader API's `PublicAuthor` carries `articleCount` and `followerCount` but
 * no views figure (magazine-api.yaml) - so the reads are LEFT OUT and the row is two
 * (B2-PREVIEW-PUBLIC). They are not in the profile, so they are the one part of the preview not
 * drawn from ./public-view.ts: they come from reads the cabinet ALREADY makes, never a new one -
 *   - articles: `listOwnArticles` for ONE `published` row and the page's own `total`, the way the
 *     sidebar counts the articles that need changes (features/shell/cabinet-attention.ts);
 *   - followers: the Overview's first read, `getDashboard` with no range - the followers tile's
 *     count, which is the count now (only its gained and lost belong to a range).
 *
 * ★ ONLY ONCE THERE ARE SOME (T28's "the figures show only once there are some"): with nothing
 * published - an applicant - there is no row. Followers nobody measures (not reported yet, a
 * failed `getDashboard`) are left out, never a zero; a refused count - a person with no author row
 * is refused both reads - draws no row and no error, because the figures are not what this page
 * is for.
 */
import { useCallback } from 'react';
import { useCabinetApi } from '../../features/cabinet/api';
import type { Dashboard } from '../../features/cabinet/api';
import { useAsync } from '../../features/cabinet/state';

export interface PublicFigures {
  /** How many articles are published, at least one. */
  readonly articles: number;
  /** The follower count - `undefined` while nobody measures it. */
  readonly followers: number | undefined;
}

/** The followers tile's count: `no_data` is a true 0, `not_reported_yet` nothing at all. */
function followersOf(dashboard: Dashboard): number | undefined {
  const tile = dashboard.tiles.followers;
  if (tile.state === 'no_data') return 0;
  return tile.state === 'ok' && typeof tile.value === 'number' ? tile.value : undefined;
}

/** The row, or `undefined` when there is nothing published (or no count was served). */
export function publicFiguresOf(
  published: number | undefined,
  dashboard: Dashboard | undefined,
): PublicFigures | undefined {
  if (published === undefined || published < 1) return undefined;
  return {
    articles: published,
    followers: dashboard === undefined ? undefined : followersOf(dashboard),
  };
}

/** The two reads, once per mount; `undefined` until they answer, and for good when refused. */
export function usePublicFigures(): PublicFigures | undefined {
  const api = useCabinetApi();
  const read = useCallback(
    async (signal: AbortSignal): Promise<PublicFigures | undefined> => {
      const [articles, dashboard] = await Promise.all([
        api.listOwnArticles({ status: ['published'], limit: 1 }, signal),
        // Without it the count still stands; the followers it carries are left out.
        api.getDashboard(undefined, signal).catch(() => undefined),
      ]);
      return publicFiguresOf(articles.total, dashboard);
    },
    [api],
  );
  const figures = useAsync(read, 'profile-preview:figures');
  return figures.status === 'ready' ? figures.data : undefined;
}
